import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { HiOutlineMapPin } from 'react-icons/hi2';
import SectionHeader from 'components/SectionHeader';
import Button from '../../../components/Button';
import {
  NEW_SESSION_ROUTE_KEY,
} from '../../RouteSelection/routePickerConstants';

interface StoredRouteSelection {
  routeId: string;
  routeDescription: string;
  gateId: string | null;
  gateDescription: string | null;
}

export default function NewSessionRouteSelector() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setValue, register } = useFormContext();
  const [selection, setSelection] = useState<
    StoredRouteSelection | null
  >(null);

  // Read from sessionStorage on mount / navigation return
  useEffect(() => {
    const stored = sessionStorage.getItem(NEW_SESSION_ROUTE_KEY);
    if (stored) {
      try {
        const parsed: StoredRouteSelection = JSON.parse(stored);
        setSelection(parsed);
        setValue('route_id', parsed.routeId);
        if (parsed.gateId) {
          setValue('gates_id', parsed.gateId);
        }
      } catch {
        sessionStorage.removeItem(NEW_SESSION_ROUTE_KEY);
      }
    }
  }, [location.key, setValue]);

  const handleSelectRoute = () => {
    navigate('/route-picker', {
      state: {
        returnTo: location.pathname,
        sessionId: '',
        isNewSession: true,
      },
    });
  };

  const handleClear = () => {
    sessionStorage.removeItem(NEW_SESSION_ROUTE_KEY);
    setSelection(null);
    setValue('route_id', '');
    setValue('gates_id', '');
  };

  return (
    <div className={
      'cr-mb-4 cr-flex cr-w-full '
      + 'cr-flex-[1_1] cr-flex-col'
    }>
      <div className="cr-h-full cr-w-full">
        <div className="cr-flex cr-flex-col">
          <SectionHeader title="Route &amp; Gates" />
          <div className={
            'cr-flex cr-flex-col cr-gap-3 cr-rounded-lg '
            + 'cr-bg-white cr-p-4 cr-shadow-md '
            + 'md:cr-w-[640px]'
          }>
            <input type="hidden" {...register('route_id')} />
            <input type="hidden" {...register('gates_id')} />

            {selection ? (
              <div className="cr-flex cr-flex-col cr-gap-2">
                <div className={
                  'cr-flex cr-items-center cr-gap-2 '
                  + 'cr-text-sm'
                }>
                  <HiOutlineMapPin
                    className="cr-h-5 cr-w-5 cr-text-green-600"
                  />
                  <span className="cr-font-medium">
                    {selection.routeDescription}
                  </span>
                </div>
                {selection.gateDescription && (
                  <div className={
                    'cr-ml-7 cr-text-sm cr-text-gray-600'
                  }>
                    Gate: {selection.gateDescription}
                  </div>
                )}
                <div className="cr-mt-1 cr-flex cr-gap-2">
                  <Button onClick={handleSelectRoute}>
                    Change
                  </Button>
                  <button
                    type="button"
                    onClick={handleClear}
                    className={
                      'cr-text-sm cr-text-gray-500 '
                      + 'hover:cr-text-gray-700'
                    }
                  >
                    Clear
                  </button>
                </div>
              </div>
            ) : (
              <div className="cr-flex cr-flex-col cr-gap-2">
                <p className={
                  'cr-text-sm cr-text-gray-500'
                }>
                  Optionally assign a route and gates
                  from a previous session.
                </p>
                <div>
                  <Button onClick={handleSelectRoute}>
                    Select Route &amp; Gates
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
